import { useMemo, useRef } from 'react';
import { useProductService, type ProductBrowseRequest } from '@skin-seoul/service';
import { isEqual } from 'es-toolkit/compat';
import { type FilterOptions, type FilterSummary } from '../types/filters';
import { buildProductBrowseParams, type RouteParams } from '../utils/collectionProductApiHelpers';
import { useProductGridCommon, useProductDataProcessing } from './shared/useProductGridCommon';

interface UseProductGridDataProps {
  category?: string;
  subcategory?: string;
  subSubcategory?: string;
  brand?: string;
  productTag?: string;
  filters?: FilterOptions;
  sortBy?: string;
  currentPage?: number;
  productsPerPage: number;
  onProductCountChange?: (count: number) => void;
  onFilterSummaryChange?: (summary: FilterSummary | undefined) => void;
}

export const useProductGridData = ({
  category,
  subcategory,
  subSubcategory,
  brand,
  productTag,
  filters = {},
  sortBy,
  currentPage = 1,
  productsPerPage,
  onProductCountChange,
  onFilterSummaryChange,
}: UseProductGridDataProps) => {
  const { currency, productService, pagination } = useProductGridCommon({
    currentPage,
    productsPerPage,
  });

  const { useGetProductBrandTaxonomy, useGetCategoryTaxonomy } = useProductService();
  const { data: brandTaxonomy } = useGetProductBrandTaxonomy();
  const { data: categoryTaxonomy } = useGetCategoryTaxonomy();

  const routeParams: RouteParams = useMemo(
    () => ({
      category,
      subcategory,
      subSubcategory,
      brand,
      productTag,
    }),
    [category, subcategory, subSubcategory, brand, productTag],
  );

  const transformedCategories = useMemo(() => {
    return categoryTaxonomy?.map((term) => ({
      termId: term.termId,
      name: term.name,
      slug: term.slug,
      subCategories: [],
    }));
  }, [categoryTaxonomy]);

  const previousResultRef = useRef<{
    normalizedParams: object;
    apiParams: ProductBrowseRequest;
  } | null>(null);

  const apiParams = useMemo(() => {
    const params = buildProductBrowseParams(
      routeParams,
      filters,
      currency,
      pagination,
      sortBy,
      undefined,
      brandTaxonomy,
      transformedCategories,
    );

    const normalizedParams = {
      brands: params.brands || '',
      categories: params.categories || '',
      currency: params.currency,
      ingredients: params.ingredients || '',
      limit: params.limit,
      maxPrice: params.maxPrice,
      minPrice: params.minPrice,
      page: params.page,
      productTagSlugs: params.productTagSlugs || '',
      publicationStatus: params.publicationStatus,
      skinConcerns: params.skinConcerns || '',
      skinTypes: params.skinTypes || '',
      sortBy: params.sortBy,
      stockStatus: params.stockStatus,
    };

    if (previousResultRef.current && isEqual(normalizedParams, previousResultRef.current.normalizedParams)) {
      return previousResultRef.current.apiParams;
    }

    previousResultRef.current = {
      normalizedParams,
      apiParams: params,
    };

    return params;
  }, [routeParams, filters, currency, pagination, sortBy, brandTaxonomy, transformedCategories]);
  const { data } = productService.useGetProductBrowse(apiParams);

  return useProductDataProcessing({
    data,
    currentPage,
    onProductCountChange,
    onFilterSummaryChange,
  });
};
